import { Avatar, FieldError, Input, Label, TextField } from "@heroui/react";
import { memo, useEffect, useState } from "react";
import { interpolate } from "@funnel-ui/interpolate";
import { DynamicIcon } from "@funnel-ui/primitives/DynamicIcon";
import { PresetPill } from "@funnel-ui/primitives/PresetPill";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

/**
 * Captures a first name (or display name) for paywall prefill only — never
 * pushed to Kinde. Stays client-side, persisted in funnel session state and
 * carried in `ob` at handoff. See docs/PLAN_CUSTOM_AUTH_FLOW.md §1, §2 step 1.
 */
export type NameCapturePreset = {
  id: string;
  label: string;
  /**
   * Optional value applied to the input on tap. When omitted, tapping just
   * fires `onPresetSelected` so the parent can resolve the value (e.g. from
   * an authenticated user's profile).
   */
  value?: string;
};

export type NameCapturePreview = {
  template: string;
  followUp?: string;
  avatarUrl?: string;
};

export type NameCaptureReason = {
  icon: string;
  title: string;
  description: string;
};

type NameCaptureProps = {
  question?: string;
  description?: string;
  /** Small uppercase label rendered above the question in accent color. */
  eyebrow?: string;
  initialValue?: string;
  /** Reject empty or whitespace-only strings. Defaults to true. */
  required?: boolean;
  /**
   * Lower bound on trimmed length. Defaults to 1 — anything that's a single
   * non-whitespace character is accepted (international names, mononyms).
   */
  minLength?: number;
  /**
   * Upper bound on raw length. Defaults to 60. Acts as a hard cap on the
   * underlying `<input maxlength>` to stop adversarial pasted blobs.
   */
  maxLength?: number;
  /**
   * Optional shortcut pills rendered above the input. Tapping a preset:
   *   - sets `value` if the preset has one,
   *   - fires `onPresetSelected(preset)` so parents can react (e.g. resolve
   *     a name from the authenticated profile and call back via parent
   *     state).
   * Use this for the "Use my first name" / "A nickname" affordance described
   * in the SecretMate funnel plan §5 Step 7.
   */
  presets?: NameCapturePreset[];
  onPresetSelected?: (preset: NameCapturePreset) => void;
  /**
   * Visual placeholder for the input. Defaults to `Your name`.
   */
  placeholder?: string;
  /** Live chat-bubble preview with {key|default} interpolation. */
  preview?: NameCapturePreview;
  /** Benefit/reason cards rendered below the input. */
  reasons?: NameCaptureReason[];
  onChange?: (value: string) => void;
  onValidityChange?: (valid: boolean) => void;
  className?: string;
  testId?: string;
};

const isNameValid = (value: string, required: boolean, minLength: number): boolean => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    // Optional field: empty is a valid answer. Required field: empty fails
    // up-front regardless of `minLength` so the error copy matches user intent.
    return !required;
  }
  return trimmed.length >= minLength;
};

type ValidityComputation = {
  valid: boolean;
  errorMessage: string | undefined;
};

const computeValidity = (
  value: string,
  required: boolean,
  minLength: number,
): ValidityComputation => {
  const trimmed = value.trim();
  const valid = isNameValid(value, required, minLength);
  // Only surface the error message once the user has typed something then
  // emptied the field — typing-then-erasing is a common path and we don't
  // want to flash an error during the natural input pause.
  const showError = required && value.length > 0 && trimmed.length === 0;
  return {
    valid,
    errorMessage: showError ? "Please enter your name." : undefined,
  };
};

const PresetRow = ({
  presets,
  activePresetId,
  onPress,
  testId,
}: {
  presets: NameCapturePreset[];
  activePresetId: string | null;
  onPress: (preset: NameCapturePreset) => void;
  testId?: string;
}) => (
  <div
    role="group"
    aria-label="Name shortcuts"
    className="flex flex-wrap items-center gap-2"
    data-testid={testId ? `${testId}-presets` : undefined}
  >
    {presets.map((preset) => (
      <PresetPill
        key={preset.id}
        label={preset.label}
        selected={activePresetId === preset.id}
        ariaPressed={activePresetId === preset.id}
        onPress={() => onPress(preset)}
        testId={testId ? `${testId}-preset-${preset.id}` : undefined}
      />
    ))}
  </div>
);

type NameCaptureState = {
  value: string;
  activePresetId: string | null;
  handleChange: (next: string) => void;
  handlePresetPress: (preset: NameCapturePreset) => void;
};

const useNameCaptureState = ({
  initialValue,
  required,
  minLength,
  onChange,
  onValidityChange,
  onPresetSelected,
}: {
  initialValue: string;
  required: boolean;
  minLength: number;
  onChange?: (value: string) => void;
  onValidityChange?: (valid: boolean) => void;
  onPresetSelected?: (preset: NameCapturePreset) => void;
}): NameCaptureState => {
  const [value, setValue] = useState(initialValue);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);

  // Report initial validity on mount so the orchestrator's ContinueButton
  // starts disabled for required empty fields (NX-9586).
  const isValid = isNameValid(value, required, minLength);
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleChange = (next: string): void => {
    setValue(next);
    setActivePresetId(null);
    onChange?.(next);
    onValidityChange?.(isNameValid(next, required, minLength));
  };

  const handlePresetPress = (preset: NameCapturePreset): void => {
    setActivePresetId(preset.id);
    if (preset.value !== undefined) {
      setValue(preset.value);
      onChange?.(preset.value);
      onValidityChange?.(isNameValid(preset.value, required, minLength));
    }
    onPresetSelected?.(preset);
  };

  return { value, activePresetId, handleChange, handlePresetPress };
};

const ChatBubblePreview = ({
  preview,
  currentName,
}: {
  preview: NameCapturePreview;
  currentName: string;
}) => {
  const text = interpolate(preview.template, { name: currentName });
  return (
    <div className="flex items-start gap-3">
      {preview.avatarUrl && (
        <Avatar size="sm" className="shrink-0">
          <Avatar.Image src={preview.avatarUrl} alt="" />
          <Avatar.Fallback>?</Avatar.Fallback>
        </Avatar>
      )}
      <div
        className="flex flex-col gap-1 rounded-2xl rounded-tl-sm px-4 py-3"
        style={{ background: "hsl(var(--brand-bg-foreground) / 0.08)" }}
      >
        <p
          className="m-0 text-sm leading-relaxed"
          style={{ color: "hsl(var(--brand-bg-foreground))" }}
        >
          {text}
        </p>
        {preview.followUp && (
          <p
            className="m-0 text-sm leading-relaxed"
            style={{ color: "hsl(var(--brand-bg-foreground) / 0.6)" }}
          >
            {preview.followUp}
          </p>
        )}
      </div>
    </div>
  );
};

const ReasonsBlock = ({ reasons }: { reasons: NameCaptureReason[] }) => (
  <ul className="flex flex-col gap-4">
    {reasons.map((reason, i) => (
      <li key={i} className="flex items-start gap-3">
        <span
          className="mt-0.5 inline-flex shrink-0 items-center justify-center rounded-lg p-2"
          style={{
            background: "hsl(var(--brand-primary) / 0.12)",
            color: "hsl(var(--brand-primary))",
          }}
        >
          <DynamicIcon icon={reason.icon} size={18} />
        </span>
        <div className="flex flex-col gap-0.5">
          <span
            className="text-sm font-semibold"
            style={{ color: "hsl(var(--brand-bg-foreground))" }}
          >
            {reason.title}
          </span>
          <span
            className="text-xs leading-relaxed"
            style={{ color: "hsl(var(--brand-bg-foreground) / 0.55)" }}
          >
            {reason.description}
          </span>
        </div>
      </li>
    ))}
  </ul>
);

const NameCaptureImpl = ({
  question = "What should we call you?",
  description,
  eyebrow,
  initialValue = "",
  required = true,
  minLength = 1,
  maxLength = 60,
  presets,
  onPresetSelected,
  placeholder = "Your name",
  preview,
  reasons,
  onChange,
  onValidityChange,
  className,
  testId,
}: NameCaptureProps) => {
  const { value, activePresetId, handleChange, handlePresetPress } = useNameCaptureState({
    initialValue,
    required,
    minLength,
    onChange,
    onValidityChange,
    onPresetSelected,
  });

  const { valid, errorMessage } = computeValidity(value, required, minLength);

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading
        title={question}
        description={description}
        eyebrow={eyebrow}
        editablePath="question"
      />

      {presets && presets.length > 0 && (
        <PresetRow
          presets={presets}
          activePresetId={activePresetId}
          onPress={handlePresetPress}
          testId={testId}
        />
      )}

      <TextField value={value} onChange={handleChange} isInvalid={!!errorMessage}>
        <Label className="sr-only">Name</Label>
        <Input
          type="text"
          inputMode="text"
          autoComplete="given-name"
          autoCapitalize="words"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          maxLength={maxLength}
          className="border-b border-[hsl(var(--brand-bg-foreground)/0.2)] pb-2 text-3xl font-light focus:border-[hsl(var(--brand-primary))]"
        />
        {errorMessage && <FieldError>{errorMessage}</FieldError>}
      </TextField>

      {preview && <ChatBubblePreview preview={preview} currentName={value} />}

      {reasons && reasons.length > 0 && <ReasonsBlock reasons={reasons} />}

      <p role="status" aria-live="polite" className="sr-only">
        {valid ? "Name valid" : (errorMessage ?? "Name required")}
      </p>
    </section>
  );
};

export const NameCapture = memo(NameCaptureImpl);

export default NameCapture;
